'use client'

import { useState } from 'react'
import { usePathname } from 'next/navigation'
import { Share2, Check, X } from 'lucide-react'
import { Button } from './Button'
import { Card, CardHeader, CardContent } from './Card'
import { APP_REGISTRY } from '@/registry/apps'
import { copyToClipboard } from '../lib/clipboard'

/**
 * "Share this app" — only rendered when the user is inside a specific app.
 *
 * The share link points a friend at /signup?next=/apps/<id>. The auth flow reads
 * that `next` target and stamps `signup_app` onto the brand-new account, so the
 * recipient starts with ONLY this app enabled (see signupNext.ts / AuthForm.tsx).
 * That's the same scoping a raw /apps/<id> deep-link gets, made explicit so the
 * recipient lands straight on sign-up rather than bouncing through the guard.
 */
export function ShareAppCard() {
  const pathname = usePathname()
  const [copied, setCopied] = useState(false)
  const [fallbackUrl, setFallbackUrl] = useState<string | null>(null)

  const currentApp = APP_REGISTRY.find(
    (app) => pathname === app.route || pathname.startsWith(app.route + '/')
  )

  // Nothing app-specific to share from the dashboard, explore, settings, etc.
  if (!currentApp) return null

  const shareUrl = () =>
    `${window.location.origin}/signup?next=${encodeURIComponent(currentApp.route)}`

  async function share() {
    const url = shareUrl()
    const title = `${currentApp!.name} — Peter's Apps`
    const text = `Check out ${currentApp!.name} on Peter's Apps`

    // Native share sheet on mobile/PWA when available; otherwise copy the link.
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share({ title, text, url })
        return
      } catch {
        // User dismissed, or share failed — fall through to copy.
      }
    }

    const ok = await copyToClipboard(url)
    if (ok) {
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } else {
      // Both share and clipboard paths blocked — surface the URL to copy manually.
      setFallbackUrl(url)
    }
  }

  return (
    <Card>
      <CardHeader>
        <h2 className="text-lg font-semibold text-gray-900">Share this app</h2>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-gray-600">
          Send a friend a link to <strong>{currentApp.name}</strong>. When they
          sign up, they&apos;ll start with just this app enabled.
        </p>
        <Button onClick={() => void share()} className="w-full justify-center">
          {copied ? (
            <Check className="w-4 h-4 mr-2" />
          ) : (
            <Share2 className="w-4 h-4 mr-2" />
          )}
          {copied ? 'Link copied' : `Share ${currentApp.name}`}
        </Button>
      </CardContent>

      {fallbackUrl && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-gray-900">Copy this link</h3>
              <button
                type="button"
                onClick={() => setFallbackUrl(null)}
                className="text-gray-400 hover:text-gray-600"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-gray-500 mb-2">
              Clipboard access is blocked in this context. Select and copy manually.
            </p>
            <input
              type="text"
              readOnly
              value={fallbackUrl}
              onFocus={(e) => e.currentTarget.select()}
              autoFocus
              className="w-full px-3 py-2 border border-gray-300 rounded font-mono text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      )}
    </Card>
  )
}
